/**
 * audible-clock — the moment of a Web Audio graph that the person is HEARING
 * right now, as opposed to the one the graph is rendering.
 *
 * ctx.currentTime is the time of the block being RENDERED. It reaches the
 * speaker outputLatency + baseLatency later. Anything that stamps "now" from a
 * human reaction (tap-to-sync of lyrics, "set to now" on a text layer) or draws
 * a picture for "now" (a lyric/visualiser preview) has to use the AUDIBLE time,
 * otherwise every stamp is late — and every preview early — by the device's
 * latency, and the exported file (which a player keeps in sync by itself)
 * disagrees with what the preview showed.
 *
 * Measured 2026-09-23 on this Windows machine, Chrome 153 and Edge 153, onboard
 * output: outputLatency 40 ms + baseLatency 10 ms; getOutputTimestamp() puts the
 * audible sample 43–52 ms behind currentTime — the same number two ways.
 * Bluetooth heads add 150–300 ms, of which the browser reports what the OS
 * tells it (profanity-censor-core.js measured 48 ms on a plain desktop card).
 *
 *   AudibleClock.lagS(ctx)             → seconds the audible sound trails the graph, [0, MAX_LAG_S]
 *   AudibleClock.audible(ctx, graphS)  → graphS − lagS(ctx), never below 0
 *   AudibleClock.heardAtPerf(ctx, perfMs) → the graph moment audible at a page-clock moment
 *                                       (an input event's timeStamp), smooth via getOutputTimestamp
 *
 * Deliberately device-only. A person's own reaction time is a different thing
 * with a different owner (see KaraokeSyncEditor.REACTION_S): mixing the two into
 * one number would hide which of them is wrong when a stamp is off.
 *
 * Exposed as window.AudibleClock and module.exports (node tests). ES5.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.AudibleClock = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* A reported latency above this is a broken value, not a device: capped so
       it can never throw a stamp further off than the problem it corrects. */
    var MAX_LAG_S = 0.5;

    function num(v) { return (typeof v === 'number' && isFinite(v) && v > 0) ? v : 0; }

    function lagS(ctx) {
        if (!ctx) return 0;
        var lag = 0;
        try {
            var out = num(ctx.outputLatency), base = num(ctx.baseLatency);
            if (out > 0) {
                // Chrome, Edge, Firefox: the two parts of the path, reported directly.
                lag = out + base;
            } else if (typeof ctx.getOutputTimestamp === 'function') {
                // Safari has no outputLatency, but does map the sample at the
                // speaker to a context time. Extrapolate that sample to NOW.
                var ts = ctx.getOutputTimestamp();
                var perfNow = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
                if (ts && num(ts.contextTime) > 0 && num(ts.performanceTime) > 0 && perfNow > 0) {
                    lag = ctx.currentTime - (ts.contextTime + (perfNow - ts.performanceTime) / 1000);
                }
                if (!(lag > 0)) lag = base * 2;   // the recording-latency fallback: two buffers
            } else {
                lag = base * 2;
            }
        } catch (e) { lag = 0; }
        if (!(lag > 0) || !isFinite(lag)) return 0;
        return Math.min(MAX_LAG_S, lag);
    }

    function audible(ctx, graphS) {
        var t = graphS - lagS(ctx);
        return t > 0 ? t : 0;
    }
    /* The graph moment that was AUDIBLE at a page-clock moment (performance.now()
       time) — an input event's timeStamp: when a pad, a key or a MIDI message
       arrived, not when a busy page got round to it. getOutputTimestamp() pairs
       the sample at the speaker with its page-clock moment, so the answer is
       smooth; audible(currentTime − age) steps with currentTime, which moves in
       whole audio callbacks (10 ms on Windows): replayed real drum performances
       (/virtual-drums, 30.10.2026) kept a 3 ms median / 8 ms p95 error that way.
       Falls back to it where the timestamp is not ready (a context not running yet,
       a browser without the call), or is off from it by more than the cap. */
    function heardAtPerf(ctx, perfMs) {
        if (!ctx) return 0;
        var now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : perfMs;
        if (!(perfMs > 0) || perfMs > now) perfMs = now;
        var ref = audible(ctx, ctx.currentTime - (now - perfMs) / 1000);
        try {
            if (ctx.state === 'running' && typeof ctx.getOutputTimestamp === 'function') {
                var ts = ctx.getOutputTimestamp();
                if (ts && num(ts.contextTime) > 0 && num(ts.performanceTime) > 0) {
                    var t = ts.contextTime + (perfMs - ts.performanceTime) / 1000;
                    if (isFinite(t) && Math.abs(t - ref) <= MAX_LAG_S) return t > 0 ? t : 0;
                }
            }
        } catch (e) {}
        return ref;
    }

    return { lagS: lagS, audible: audible, heardAtPerf: heardAtPerf, MAX_LAG_S: MAX_LAG_S };
});
